import React, { useState, useEffect } from "react";
import {
  Modal,
  Text,
  Button,
  Stack,
  Group,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBrandChrome,
  IconBrandSafari,
  IconCheck,
} from "@tabler/icons-react";
import { isMobile, isFirefox, isIOS } from "react-device-detect";

const FirefoxCameraWarning = ({ onContinue }) => {
  if (!(isMobile && isFirefox)) {
    return null;
  }

  const [opened, setOpened] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const url = window.location.href;

  useEffect(() => {
    const handleError = (event) => {
      setError(event.message || String(event.error || event));
    };
    const handleRejection = (event) => {
      setError(event.reason?.message || String(event.reason));
    };
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  const openInChrome = () => {
    let chromeUrl;
    if (isIOS) {
      chromeUrl = url.replace(/^https?:\/\//, (match) =>
        match === "https://" ? "googlechromes://" : "googlechrome://"
      );
    } else {
      chromeUrl = `intent://${url.replace(/^https?:\/\//, "")}#Intent;scheme=https;package=com.android.chrome;end`;
    }
    window.location.href = chromeUrl;
  };

  const openInSafari = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  const handleContinue = () => {
    setOpened(false);
    onContinue?.();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleContinue}
      withCloseButton
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      trapFocus={false}
      padding="lg"
    >
      <Stack spacing="lg" align="center">
        {copied ? (
          <>
            <IconCheck size={48} />
            <Stack spacing={4} align="center">
              <Text align="center" size="lg" weight={500}>
                Link copied to clipboard
              </Text>
              <Text align="center">
                Now go and put this into Safari on your phone.
              </Text>
            </Stack>
          </>
        ) : (
          <>
            <Group spacing={4} position="center">
              <ThemeIcon size={72} radius="md" variant="light" color="gray">
                <IconBrandSafari
                  size={40}
                  color="#000"
                  aria-hidden="true"
                />
              </ThemeIcon>
              <ThemeIcon size={72} radius="md" variant="light" color="gray">
                <IconBrandChrome
                  size={40}
                  color="#4285F4"
                  aria-hidden="true"
                />
              </ThemeIcon>
            </Group>
            <Stack spacing={4} align="center">
              <Text align="center" size="xl" weight={700}>
                Firefox may not support camera features needed for verification.
              </Text>
              <Text align="center">
                For best results, please use Safari or Chrome.
              </Text>
            </Stack>
            {error && (
              <Text color="red" align="center" size="sm">
                {error}
              </Text>
            )}
            <Stack spacing="sm" w="100%">
              <Button
                autoFocus={false}
                size="lg"
                fullWidth
                leftIcon={<IconBrandChrome />}
                onClick={openInChrome}
                aria-label="Open link in Chrome"
                styles={{
                  root: {
                    backgroundColor: "#4285F4",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#357AE8" },
                    "&:focus": {
                      outline: "2px solid #2a56c6",
                      outlineOffset: 2,
                    },
                  },
                }}
              >
                Open in Chrome
              </Button>
              {isIOS && (
                <Button
                  autoFocus={false}
                  size="lg"
                  fullWidth
                  variant="filled"
                  leftIcon={<IconBrandSafari />}
                  onClick={openInSafari}
                  aria-label="Copy link for Safari"
                  styles={{
                    root: {
                      backgroundColor: "#000",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#333" },
                      "&:focus": {
                        outline: "2px solid #000",
                        outlineOffset: 2,
                      },
                    },
                  }}
                >
                  Copy link for Safari
                </Button>
              )}
              <Button
                autoFocus={false}
                variant="subtle"
                color="gray"
                size="lg"
                fullWidth
                onClick={handleContinue}
                aria-label="Continue on this device"
                styles={{
                  root: {
                    "&:focus": {
                      outline: "2px solid #868e96",
                      outlineOffset: 2,
                    },
                  },
                }}
              >
                Continue on this device
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default FirefoxCameraWarning;
